import * as React from "react";
import * as Palette from "../../../../palette";
import { PageLayout } from "../../../../palette/layout/page-layout";
import { logger } from "../../../../core";
import { AddStashKeyModal } from "./add-stash-key-modal";

import RemoveMicrosEnvironmentVariableMutation from "../models/remove-micros-environment-variable.mutation.graphql";
import MicrosServiceWithVariablesQuery from "../models/micros-service-with-variables.query.graphql";
import { withLoadingSpinner } from "../../../../palette/with-loading-spinner";
import { withServiceNotFoundHandler } from "../../../../palette/with-service-not-found-handler";
import { withGraphqlErrorFlags } from "../../../../palette/with-graphql-error-flags";
import { EditableTags } from "../../../../palette/editable";
import { TagProps } from "../../../../palette/editable/editable-tags";
import {
  addMicrosEnvironmentVariable,
  addMicrosEnvironmentVariableVariables
} from "../models/__generated__/addMicrosEnvironmentVariable";
import {
  removeMicrosEnvironmentVariable,
  removeMicrosEnvironmentVariableVariables
} from "../models/__generated__/removeMicrosEnvironmentVariable";
import {
  microsServiceWithVariables,
  microsServiceWithVariables_service
} from "../models/__generated__/microsServiceWithVariables";
import {
  withSufficientServicePermission,
  Permission,
  withShowFlag,
  compose,
  ChildProps,
  MutationFunc,
  graphql,
  getErrorMessage,
  FlagProps
} from "@atlassiansox/microscopekit";
import { isForbiddenError } from "../../../../core/helpers";
import { Tc2Error } from "../../../../core/components/tc2";

const AkSpinner = require("@atlaskit/spinner").default;
const { ButtonGroup } = require("@atlaskit/button");

export type ServiceStashKeysProps = ChildProps<
  {
    addMicrosEnvironmentVariable: MutationFunc<
      addMicrosEnvironmentVariable,
      addMicrosEnvironmentVariableVariables
    >;
    removeMicrosEnvironmentVariable: MutationFunc<
      removeMicrosEnvironmentVariable,
      removeMicrosEnvironmentVariableVariables
    >;
  } & FlagProps,
  microsServiceWithVariables
>;

interface State {
  environmentIdInput: string;
  keyInput: string;
  valueInput: string;
  addInProgress: boolean;
  deleteInProgress: { [key: string]: boolean };
  addError?: string;
  showAddModal: boolean;
}

export class ServiceStashKeysView extends React.Component<
  ServiceStashKeysProps,
  State
> {
  constructor(props: ServiceStashKeysProps) {
    super(props);
    this.state = {
      environmentIdInput: "",
      keyInput: "",
      valueInput: "",
      addInProgress: false,
      showAddModal: false,
      deleteInProgress: {}
    };
  }

  componentDidMount() {
    document.title = this.props.data!.service!.id + " | Stash";
  }

  componentWillUnmount() {
    document.title = "Microscope";
  }

  render() {
    const service = this.props.data!.service!;
    const listEnvironments = this.props.data!.listEnvironments!;
    const { micros } = service;

    const environmentIds = listEnvironments.map(({ id }) => id);

    const actions = (
      <ButtonGroup>
        <Palette.Button onClick={() => this.setState({ showAddModal: true })}>
          Add Stash Key
        </Palette.Button>
      </ButtonGroup>
    );

    return (
      <PageLayout title="Stash Keys" actions={actions}>
        {this.state.showAddModal ? (
          <AddStashKeyModal
            serviceId={service.id}
            environments={environmentIds}
            closeModal={() => this.setState({ showAddModal: false })}
          />
        ) : null}
        <Palette.Table
          format={["left", "left", "small"]}
          head={["Keys", "Environments", ""]}
          cells={
            micros
              ? this.getGroupedVariables(service).map(
                  ({ key, environments }) => {
                    const tags = Object.keys(environments).map(
                      environmentId => ({
                        label: environmentId,
                        value: environmentId,
                        isSelected: true
                      })
                    );
                    return [
                      <span>{key}</span>,
                      <EditableTags
                        tags={tags}
                        onDelete={async (environmentId: TagProps) =>
                          this.onDelete(
                            environments,
                            [environmentId.value],
                            service.id,
                            key
                          )
                        }
                      />,
                      <span>
                        <Palette.Button
                          key={key}
                          onClick={() =>
                            this.onDelete(
                              environments,
                              environmentIds,
                              service.id,
                              key
                            )
                          }
                          className="delete-key"
                          iconBefore={
                            this.state.deleteInProgress[key] ? (
                              <AkSpinner size="small" isCompleting={false} />
                            ) : null
                          }
                          disabled={this.state.deleteInProgress[key]}
                        >
                          Delete All
                        </Palette.Button>
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                      </span>
                    ];
                  }
                )
              : []
          }
          emptyMessage="There are no stash keys"
        />
      </PageLayout>
    );
  }

  async onDelete(
    environments: { [key: string]: string },
    environmentIds: string[],
    serviceId: string,
    variableKey: string
  ) {
    track("delete key", { category: "info", element: "service stash keys" });

    let confirmMessage =
      `Delete variable ${variableKey} from ` +
      `${environmentIds.length > 1 ? "all environments" : environmentIds[0]}?`;
    if (!Palette.confirm(confirmMessage)) {
      track("delete key cancelled", {
        category: "info",
        element: "service team management"
      });
      return;
    }

    try {
      this.setState(current => {
        let deleteInProgress = { ...current.deleteInProgress };
        deleteInProgress[variableKey] = true;
        return { deleteInProgress };
      });

      await Promise.all(
        environmentIds
          .filter(environmentId => environments[environmentId])
          .map(environmentId =>
            this.props.removeMicrosEnvironmentVariable({
              variables: {
                id: environments[environmentId]
              }
            })
          )
      );

      this.setState(current => {
        let deleteInProgress = { ...current.deleteInProgress };
        delete deleteInProgress[variableKey];
        return { deleteInProgress };
      });
      track("delete key successfully finished", {
        category: "info",
        element: "service stash keys"
      });
    } catch (e) {
      track("delete key failed", {
        category: "error",
        element: "service stash keys"
      });

      const errMessage = getErrorMessage(e);

      const errComponent = isForbiddenError(errMessage) ? (
        <Tc2Error serviceId={serviceId} />
      ) : (
        <p>{errMessage}</p>
      );
      this.props.showFlag({
        type: "error",
        title: "Failed to delete stash key",
        message: errComponent,
        disableAutoDismiss: true
      });
    }
  }

  protected getGroupedVariables(service: microsServiceWithVariables_service) {
    if (!service.micros) {
      return [];
    }
    const groupedVariables: {
      key: string;
      environments: { [key: string]: string };
    }[] = [];
    for (let environment of service.micros.environments) {
      if (environment && environment.variables) {
        for (let variable of environment.variables) {
          let v = groupedVariables.find(({ key }) => key === variable.key);
          if (!v) {
            v = { key: variable.key, environments: {} };
            groupedVariables.push(v);
          }

          v.environments[environment.env] = variable.id;
        }
      }
    }

    groupedVariables.sort((a, b) =>
      a.key < b.key ? -1 : a.key > b.key ? 1 : 0
    );
    return groupedVariables;
  }
}

export const ServiceStashKeys = compose(
  withSufficientServicePermission(Permission.Write),
  graphql(RemoveMicrosEnvironmentVariableMutation, {
    name: "removeMicrosEnvironmentVariable"
  }),
  graphql(MicrosServiceWithVariablesQuery, {
    options: () => ({
      errorPolicy: "all"
    })
  }),
  withLoadingSpinner,
  withShowFlag,
  withGraphqlErrorFlags,
  withServiceNotFoundHandler
)(ServiceStashKeysView);
